import React, { useEffect } from 'react';
import { Button, Guideline, Toast, useFirstRender, useQueryParams } from '@insurancemenu/imenu-components-lib';
import { Add } from '@insurancemenu/imenu-components-lib/dist/icons';
import { observer } from 'mobx-react-lite';
import { useRootStore } from 'store/rootStore';
import { toJS } from 'mobx';
import { subscriberClassTypes } from 'global/utils/election/subscriberClassHelper';
import { ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import { useHistory, useParams } from 'react-router-dom';
import FormsSpinner from 'components/common/loaders/FormSpinner';
import { EeClassesTypes, EeClass } from 'types/electionService';
import EeClassesGuidelines from '../EeClassesGuidelines';
import { createStyles } from './styles';
import EeClassTypeTitle from '../EeClassTypeTitle';
import EeClassBlock from '../EeClassBlock/index';

const EeClassesContent = observer((): JSX.Element => {
  const classes = createStyles();
  const {
    eeClassesStore: {
      toastErrorMessage,
      employeeClasses,
      loading,
      showErrorToast,
      setErrorToastStatus,
      getEeClasses,
      addClass,
      saveClasses,
      changeSavingTrigger,
      getEmployeeClassesWithEe,
      checkedElementsAmount,
    },
  } = useRootStore();

  const { periodId } = useParams<{ periodId: string }>();
  const query = useQueryParams();
  const groupId = query.get('groupId') || '';
  const history = useHistory();
  const firstRender = useFirstRender();

  const convertedClasses = employeeClasses?.map((classInfo) => toJS(classInfo)).slice();

  useEffect(() => {
    void getEeClasses(groupId, periodId);
    void getEmployeeClassesWithEe(groupId, periodId);
  }, []);

  useEffect(() => {
    // when all fields will be updated and valid - make request
    if (checkedElementsAmount === convertedClasses.length && !firstRender) {
      const areClassesValid = convertedClasses.every(({ name, validationMessage }) => !validationMessage && name);
      if (areClassesValid) {
        void saveClasses(groupId, periodId);
        history.push(`/product-classes/${periodId}?groupId=${groupId}`);
      }
    }
  }, [checkedElementsAmount]);

  const onToastClose = (): void => {
    setErrorToastStatus(false);
  };

  const classesSection = subscriberClassTypes.map((type: EeClassesTypes): JSX.Element => {
    // Get only classes with the same class type
    const eeClassesInType = convertedClasses.filter((classInfo: EeClass) => classInfo.type === type);

    // Create class block with remove button
    const classesList = eeClassesInType.map(({ name, id, periodId: epId, validationMessage }, index) => {
      return (
        <EeClassBlock
          key={`classItem-${type}-${name}-${id}`}
          classIndex={index + 1}
          classInfo={{ type, name, id, periodId: epId || periodId, validationMessage, groupId }}
        />
      );
    });

    return (
      <div key={`typeSection-${type}`} className={classes.section}>
        <EeClassTypeTitle type={type} />
        {/* Block  for 1 class in 1 type */}
        <div className={classes.classTypeBlock}>{classesList}</div>
        <Button
          leftIconProp={<Add />}
          type="text"
          color="primary"
          className={classes.addClassBtn}
          onClick={(): void => {
            addClass(type, periodId, groupId);
          }}
        >
          Add class
        </Button>
      </div>
    );
  });

  return (
    <>
      <div className={classes.eeClassContentWrapper}>
        <h2 className={classes.createClassTitle}>Create Employee Classes</h2>
        <p className={classes.createClassSubtitle}>Start by creating classes that employees should be grouped into. </p>
        <Guideline content={<EeClassesGuidelines />} className={classes.eeGuideline} />
        {loading ? (
          <FormsSpinner display={loading} />
        ) : (
          <>
            <div className={classes.typeSections}>{classesSection}</div>
            <div className={classes.buttonsWrapper}>
              <Button
                type="outlined"
                size="large"
                disabled={loading}
                onClick={(): void => {
                  history.goBack();
                }}
              >
                Cancel
              </Button>
              <Button
                color="primary"
                size="large"
                disabled={loading}
                onClick={(): void => {
                  changeSavingTrigger();
                }}
              >
                Save Classes
              </Button>
            </div>
          </>
        )}
      </div>
      <Toast show={showErrorToast} onClose={onToastClose} colorType={ToastColorTypes.WARNING}>
        {toastErrorMessage}
      </Toast>
    </>
  );
});

export default EeClassesContent;
